import type { PostFormValues } from "@circle-react/components/QuickPostV3/data/types";
import { Uploader } from "@circle-react/components/shared/Uploader";
import { blobPath } from "@circle-react/helpers/urlHelpers";
import {
  Dropzone,
  type DropzoneFile,
} from "@circleco/compass/components/Dropzone";
import { useCallback, useMemo, useState } from "react";
import type { FieldPath } from "react-hook-form";
import { useFormContext } from "react-hook-form";

const toSignedId = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

interface SignedIdImageDropzoneProps {
  description?: string;
  fieldName: FieldPath<PostFormValues>;
  fileLabel: string;
  testId: string;
  title: string;
  uploadFieldName: string;
}

export const SignedIdImageDropzone = ({
  description,
  fieldName,
  fileLabel,
  testId,
  title,
  uploadFieldName,
}: SignedIdImageDropzoneProps) => {
  const { setValue, watch } = useFormContext<PostFormValues>();
  const signedId = toSignedId(watch(fieldName));
  const [pendingFiles, setPendingFiles] = useState<DropzoneFile[]>([]);

  const dropzoneValue = useMemo(() => {
    if (pendingFiles.length > 0) return pendingFiles;
    if (!signedId) return [];

    return [
      {
        url: blobPath({ signed_id: signedId }),
        name: fileLabel,
      },
    ];
  }, [fileLabel, pendingFiles, signedId]);

  const uploadFile = useCallback(
    (file: File) => {
      const uploader = new Uploader(
        file,
        (_blobUrl, nextSignedId) => {
          setValue(fieldName, nextSignedId, { shouldDirty: true });
          setPendingFiles([]);
        },
        "",
        uploadFieldName,
        () => {},
        undefined,
        () => {
          setPendingFiles([]);
        },
      );
      uploader.start();
    },
    [fieldName, setValue, uploadFieldName],
  );

  const handleValueChange = useCallback(
    (files: DropzoneFile[]) => {
      if (files.length === 0) {
        setValue(fieldName, null, { shouldDirty: true });
        setPendingFiles([]);
        return;
      }

      setPendingFiles(files);
      const file = files[0]?.file;
      if (file) uploadFile(file);
    },
    [fieldName, setValue, uploadFile],
  );

  return (
    <Dropzone
      value={dropzoneValue}
      onValueChange={handleValueChange}
      title={title}
      description={description}
      data-testid={testId}
    />
  );
};
